// src/pages/Pricing/PurchaseCredits.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { createOneTimePaymentSession } from '../../services/chatApi';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { Loader, ChevronDown, ChevronUp } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const Accordion = ({ title, children }) => {
    const { t, i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="border border-slate-600 rounded-md mb-4 overflow-hidden">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full p-4 text-left bg-slate-700 hover:bg-slate-600 transition-colors flex justify-between items-center"
                aria-expanded={isOpen}
            >
                <span className="font-semibold text-white">{title}</span>
                {isOpen ? (
                    <ChevronUp className="h-5 w-5 text-white" />
                ) : (
                    <ChevronDown className="h-5 w-5 text-white" />
                )}
            </button>
            {isOpen && (
                <div className="p-4 bg-slate-800 text-white overflow-y-auto max-h-96">
                    <div className="pt-16 -mt-16">
                    {children}
                    </div>
                </div>
            )}
        </div>
    );
};

const PurchaseCredits = () => {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [amount, setAmount] = useState(10.0);
    const [consentGiven, setConsentGiven] = useState(false);
    const [isStripeLoading, setIsStripeLoading] = useState(false);
    const [error, setError] = useState('');

    const creditsToAdd = Math.floor(amount * 67);
    const currentLanguage = i18n.language;

    const handleStripePurchase = async () => {
        if (!consentGiven) return;
        setIsStripeLoading(true);
        setError('');
        try {
            const response = await createOneTimePaymentSession(amount);
            if (response.url) {
                window.location.href = response.url;
            } else {
                throw new Error('No checkout URL returned.');
            }
        } catch (err) {
            setError(err.message || 'An error occurred.');
            setIsStripeLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-brand-dark flex items-center justify-center p-4 text-white pt-32">
            <div className="max-w-lg w-full bg-slate-800 p-8 rounded-lg">
                <h1 className="text-3xl font-bold text-brand-orange mb-2">{t('purchaseCredits.title')}</h1>
                <p className="mb-6 text-slate-300">{t('purchaseCredits.currentBalance')}: <span className="font-bold">{user?.credits}</span></p>

                {/* Amount Input */}
                <div className="mb-4">
                    <label htmlFor="amount" className="block text-sm font-medium mb-1">{t('purchaseCredits.amountLabel')} (€)</label>
                    <input
                        type="number"
                        id="amount"
                        min="10"
                        step="5"
                        value={amount}
                        onChange={(e) => setAmount(parseFloat(e.target.value) || 10)}
                        className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md focus:ring-2 focus:ring-brand-orange"
                    />
                </div>

                {/* Credits Calculation Display */}
                <div className="p-4 bg-brand-dark rounded-md text-center mb-6">
                    <p>{t('purchaseCredits.youWillReceive')} <strong className="text-brand-orange text-lg">{creditsToAdd}</strong> {t('purchaseCredits.credits')}</p>
                </div>

                {/* Consent Checkbox */}
                <div className="mb-6">
                    <label htmlFor="consent" className="flex items-start text-sm text-slate-300 cursor-pointer">
                        <input
                            id="consent"
                            type="checkbox"
                            checked={consentGiven}
                            onChange={() => setConsentGiven(!consentGiven)}
                            className="h-4 w-4 mt-1 rounded border-slate-500 bg-slate-700 text-brand-orange focus:ring-brand-orange"
                        />
                        <span className="ml-3">{t('pricing.consentText')}</span>
                    </label>
                </div>
                
                {error && <p className="text-red-500 text-center mb-4">{error}</p>}
                
                {/* Payment Buttons */}
                <div className={`transition-opacity ${consentGiven ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                    <div className="flex flex-col gap-4">
                        {/* Stripe Button */}
                        <button
                            onClick={handleStripePurchase}
                            disabled={isStripeLoading || !consentGiven}
                            className="w-full flex justify-center items-center py-3 px-4 bg-slate-700 rounded-md font-semibold hover:bg-slate-600 transition-colors"
                        >
                            {isStripeLoading ? <Loader className="animate-spin" /> : `💳 ${t('purchaseCredits.stripeButton')}`}
                        </button>

                        {/* PayPal Button */}
                        <PayPalButtons
                            style={{ layout: "vertical", label: "pay" }}
                            disabled={!consentGiven}
                            createOrder={(data, actions) => {
                                return actions.order.create({
                                    purchase_units: [{
                                        amount: {
                                            value: amount.toFixed(2),
                                            currency_code: 'EUR'
                                        },
                                        custom_id: user.email,
                                        description: `${creditsToAdd} Ugotai Credits`
                                    }]
                                });
                            }}
                            onApprove={async (data, actions) => {
                                const order = await actions.order.capture();
                                console.log("PayPal Order Approved:", order);
                                // This is where you would call your backend to verify the payment
                                // and update the user's credits. For now, we'll just navigate.
                                navigate('/payment/success');
                            }}
                            onError={(err) => {
                                console.error("PayPal Error:", err);
                                setError('PayPal transaction failed.');
                            }}
                        />
                    </div>
                </div>

                <Accordion title={t("why_consent_title")}>
                    {/* German Content */}
                    {currentLanguage === 'de' && (
                        <div className="prose prose-invert max-w-none">
                            <div className="p-5">
                                <h3 className="text-xl text-brand-orange font-extrabold leading-tight mb-3">Widerrufsbelehrung</h3>
                                <h4 className="text-lg text-brand-orange font-extrabold leading-tight mb-2">Widerrufsrecht:</h4>
                                <p className="text-slate-300 font-light text-base mb-3">Sie haben das Recht, binnen 14 Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen. Die Widerrufsfrist beträgt 14 Tage ab dem Tag des Vertragsabschlusses.</p>
                                
                                <h4 className="text-lg text-brand-orange font-extrabold leading-tight mb-2">Ausübung des Widerrufsrechts:</h4>
                                <p className="text-slate-300 font-light text-base mb-3">Um Ihr Widerrufsrecht auszuüben, müssen Sie uns, der Caaise GmbH, Ritterstraße 8, 33602 Bielefeld, E-Mail: ugotai@caaise.com, mittels einer eindeutigen Erklärung (z.B. einen mit der Post versandter Brief oder eine E-Mail) über Ihren Entschluss, diesen Vertrag zu widerrufen, informieren. 
                                Zur Wahrung der Widerrufsfrist reicht es aus, dass Sie die Mitteilung über die Ausübung des Widerrufsrechts vor Ablauf der Widerrufsfrist absenden.</p>
                                
                                <h4 className="text-lg text-brand-orange font-extrabold leading-tight mb-2">Folgen des Widerrufs:</h4>
                                <p className="text-slate-300 font-light text-base mb-3">Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von Ihnen erhalten haben, unverzüglich und spätestens binnen 14 Tagen ab dem Tag zurückzuzahlen, an dem die Mitteilung über Ihren Widerruf dieses Vertrages bei uns eingegangen ist. Für diese Rückzahlung verwenden wir dasselbe Zahlungsmittel, dass Sie bei der ursprünglichen Transaktion eingesetzt haben, es sei denn, mit Ihnen wurde ausdrücklich etwas anderes vereinbart. In keinem Fall werden Ihnen wegen dieser Rückzahlung Entgelte berechnet.</p>
                                
                                <h4 className="text-lg text-brand-orange font-extrabold leading-tight mb-2">Besonderer Hinweis für digitale Inhalte/digitale Dienste (z.B. Credits zur Nutzung von KI-Tools):</h4>
                                <p className="text-slate-300 font-light text-base mb-3">Da wir Ihnen digitale Credits zur Nutzung unserer Plattform mit KI-Tools unmittelbar nach Vertragsschluss zur Verfügung stellen, erlischt Ihr Widerrufsrecht vorzeitig, wenn Sie ausdrücklich zustimmen, dass wir vor Ablauf der Widerrufsfrist mit der Ausführung des Vertrages beginnen und Sie darüber informiert wurden, dass Sie Ihr Widerrufsrecht dadurch verlieren. Die hier im Bestellprozess vorzusehende Zustimmung lautet: „Ja, ich stimme ausdrücklich zu, dass ugotai bereits vor Ablauf der 14-tägigen Widerrufsfrist mit der Bereitstellung der Credits beginnt. Mir ist bekannt, dass ich mein Widerrufsrecht in diesem Fall verliere.“</p>
                                
                                <hr className="border-slate-600 my-4" />
                                
                                <h4 className="text-lg text-brand-orange font-extrabold leading-tight mb-2">Muster-Widerrufsformular</h4>
                                <p className="text-slate-300 font-light text-base">
                                    (Wenn Sie den Vertrag widerrufen wollen, füllen Sie dieses Formular aus und senden Sie es zurück):
                                    <br /><br />
                                    An<br />
                                    Caaise GmbH<br />
                                    Ritterstr. 8<br />
                                    33602 Bielefeld<br />
                                    email: ugotai@caaise.com
                                    <br /><br />
                                    Hiermit widerrufe(n) ich/wir den von mir/uns abgeschlossenen Vertrag und die Bereitstellung der folgenden digitalen Inhalte/digitalen Dienste (z.B. Credits zur Nutzung von KI-Tools):
                                    <br /><br />
                                    Bestellt am/Erhalten am:
                                    <br />
                                    Name des/der Verbraucher(s):
                                    <br />
                                    Anschrift des/der Verbraucher(s):
                                    <br />
                                    Unterschrift des/der Verbraucher(s) (nur bei Mitteilung auf Papier):
                                    <br />
                                    Datum:
                                </p>
                            </div>
                        </div>
                    )}

                    {/* French Content */}
                    {currentLanguage === 'fr' && (
                        <div className="prose prose-invert max-w-none">
                            <div className="p-5">
                                <h3 className="text-xl text-brand-orange font-extrabold leading-tight mb-3">Information concernant l'exercice du droit de rétractation</h3>
                                <h4 className="text-lg text-brand-orange font-extrabold leading-tight mb-2">Droit de rétractation:</h4>
                                <p className="text-slate-300 font-light text-base mb-3">Vous avez le droit de vous rétracter du présent contrat dans un délai de 14 jours sans donner de motif. Le délai de rétractation expire 14 jours après le jour de la conclusion du contrat.</p>
                                
                                <h4 className="text-lg text-brand-orange font-extrabold leading-tight mb-2">Exercice du droit de rétractation:</h4>
                                <p className="text-slate-300 font-light text-base mb-3">Pour exercer votre droit de rétractation, vous devez nous notifier, Caaise GmbH, Ritterstraße 8, 33602 Bielefeld, Allemagne, e-mail : ugotai@caaise.com, votre décision de rétractation du présent contrat au moyen d'une déclaration dénuée d'ambiguïté (par exemple, lettre envoyée par la poste ou courrier électronique).
                                Pour que le délai de rétractation soit respecté, il suffit que vous transmettiez votre communication relative à l'exercice du droit de rétractation avant l'expiration du délai de rétractation.</p>
                                
                                <h4 className="text-lg text-brand-orange font-extrabold leading-tight mb-2">Conséquences de la rétractation:</h4>
                                <p className="text-slate-300 font-light text-base mb-3">En cas de rétractation de votre part du présent contrat, nous vous rembourserons tous les paiements reçus de vous, sans retard excessif et, en tout état de cause, au plus tard 14 jours à compter du jour où nous sommes informés de votre décision de rétractation du présent contrat. Nous procéderons au remboursement en utilisant le même moyen de paiement que celui que vous aurez utilisé pour la transaction initiale, sauf si vous convenez expressément d'un moyen différent ; en tout état de cause, ce remboursement n'occasionnera pas de frais pour vous.</p>
                                
                                <h4 className="text-lg text-brand-orange font-extrabold leading-tight mb-2">Avis particulier pour les contenus numériques/services numériques (par ex. crédits pour l'utilisation d'outils d'IA):</h4>
                                <p className="text-slate-300 font-light text-base mb-3">Étant donné que nous mettons à votre disposition des crédits numériques pour l'utilisation de notre plateforme avec des outils d'IA immédiatement après la conclusion du contrat, votre droit de rétractation expire prématurément si vous consentez expressément à ce que nous commencions l'exécution du contrat avant la fin du délai de rétractation et que vous avez reconnu que vous perdez en conséquence votre droit de rétractation. La déclaration de consentement à fournir dans le processus de commande est la suivante : “Oui, je consens expressément à ce que ugotai commence la fourniture des crédits avant l'expiration du délai de rétractation de 14 jours. Je reconnais que je perds mon droit de rétractation dans ce cas.”</p>
                                
                                <hr className="border-slate-600 my-4" />
                                
                                <h4 className="text-lg text-brand-orange font-extrabold leading-tight mb-2">Modèle de formulaire de rétractation</h4>
                                <p className="text-slate-300 font-light text-base">
                                    (Veuillez compléter et renvoyer le présent formulaire uniquement si vous souhaitez vous rétracter du contrat.)
                                    <br /><br />
                                    À l'attention de<br />
                                    Caaise GmbH<br />
                                    Ritterstr. 8<br />
                                    33602 Bielefeld<br />
                                    Allemagne<br />
                                    e-mail : ugotai@caaise.com
                                    <br /><br />
                                    Je/Nous (*) vous notifie/notifions (*) par la présente ma/notre (*) rétractation du contrat portant sur la fourniture du contenu numérique/service numérique suivant (par ex. crédits pour l'utilisation d'outils d'IA) :
                                    <br /><br />
                                    Commandé le (*)/Reçu le (*):
                                    <br />
                                    Nom du (des) consommateur(s):
                                    <br />
                                    Adresse du (des) consommateur(s):
                                    <br />
                                    Signature du (des) consommateur(s) (uniquement en cas de notification du présent formulaire sur papier):
                                    <br />
                                    Date:
                                    <br /><br />
                                    (*) Rayer la mention inutile.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* English Content (Default) */}
                    {(currentLanguage !== 'de' && currentLanguage !== 'fr') && (
                        <div className="prose prose-invert max-w-none">
                            <div className="p-5">
                                <h3 className="text-xl text-brand-orange font-extrabold leading-tight mb-3">Information on the Right of Withdrawal</h3>
                                <h4 className="text-lg text-brand-orange font-extrabold leading-tight mb-2">Right of Withdrawal:</h4>
                                <p className="text-slate-300 font-light text-base mb-3">You have the right to withdraw from this contract within 14 days without giving any reason. The withdrawal period will expire after 14 days from the day of the conclusion of the contract.</p>
                                
                                <h4 className="text-lg text-brand-orange font-extrabold leading-tight mb-2">Exercising the Right of Withdrawal:</h4>
                                <p className="text-slate-300 font-light text-base mb-3">To exercise your right of withdrawal, you must inform us, Caaise GmbH, Ritterstraße 8, 33602 Bielefeld, Germany, email: ugotai@caaise.com, of your decision to withdraw from this contract by a clear statement (e.g., a letter sent by post or an email).
                                To meet the withdrawal deadline, it is sufficient for you to send your communication concerning your exercise of the right of withdrawal before the withdrawal period has expired.</p>
                                
                                <h4 className="text-lg text-brand-orange font-extrabold leading-tight mb-2">Consequences of Withdrawal:</h4>
                                <p className="text-slate-300 font-light text-base mb-3">If you withdraw from this contract, we shall reimburse to you all payments received from you, without undue delay and in any event not later than 14 days from the day on which we are informed about your decision to withdraw from this contract. We will carry out such reimbursement using the same means of payment as you used for the initial transaction, unless you have expressly agreed otherwise; in any event, you will not incur any fees as a result of such reimbursement.</p>
                                
                                <h4 className="text-lg text-brand-orange font-extrabold leading-tight mb-2">Special Notice for Digital Content/Digital Services (e.g., Credits for the use of AI tools):</h4>
                                <p className="text-slate-300 font-light text-base mb-3">As we provide you with digital credits for the use of our platform with AI tools immediately after the contract is concluded, your right of withdrawal expires prematurely if you expressly consent to us beginning the performance of the contract before the end of the withdrawal period and you have acknowledged that you consequently lose your right of withdrawal. The declaration of consent required in the order process is as follows: “Yes, I expressly agree that ugotai may begin providing the credits before the 14-day withdrawal period expires. I am aware that I lose my right of withdrawal in this case.”</p>
                                
                                <hr className="border-slate-600 my-4" />
                                
                                <h4 className="text-lg text-brand-orange font-extrabold leading-tight mb-2">Model Withdrawal Form</h4>
                                <p className="text-slate-300 font-light text-base">
                                    (If you wish to withdraw from the contract, please complete and return this form.)
                                    <br /><br />
                                    To<br />
                                    Caaise GmbH<br />
                                    Ritterstr. 8<br />
                                    33602 Bielefeld<br />
                                    Germany<br />
                                    email: ugotai@caaise.com
                                    <br /><br />
                                    I/We (*) hereby give notice that I/We (*) withdraw from my/our (*) contract for the provision of the following digital content/digital services (e.g., Credits for the use of AI tools):
                                    <br /><br />
                                    Ordered on (*)/Received on (*):
                                    <br />
                                    Name of consumer(s):
                                    <br />
                                    Address of consumer(s):
                                    <br />
                                    Signature of consumer(s) (only if this form is notified on paper):
                                    <br />
                                    Date:
                                    <br /><br />
                                    (*) Delete as appropriate.
                                </p>
                            </div>
                        </div>
                    )}
                </Accordion>

                {/* Additional Information Section */}
                <div className="mt-6">
                    <h3 className="text-xl text-white font-extrabold leading-tight mb-3">{t('About Credits')}</h3>
                    <p className="text-slate-300 font-light text-base mb-6">{t('purchaseCredits.instruction_credits')}</p>

                    <hr className="border-slate-600 my-4" />

                    <p className="text-brand-orange font-light text-base">
                        
                        <label htmlFor="agree_terms" className="text-sm text-gray-300">
                                {t('signup.form.agree_to')}{' '}
                                <Link to="/terms" className="hover:underline text-orange-400">
                                    {t('terms_of_service')}
                                </Link>
                                {' & '}
                                <Link to="/privacy" className="hover:underline text-orange-400">
                                    {t('privacy_policy')}
                                </Link>
                            </label>{' '}
                        
                    </p>

                    <p className="text-brand-orange font-light text-base mt-4">
                        {t('purchaseCredits.instruction_payments')}
                    </p>
                </div>

            </div>
        </div>
    );
};

export default PurchaseCredits;
